// Header pill that shows site-wide live activity. Connects to the
// /ws/presence WebSocket and listens for `{type:'presence', live, battles}`
// events pushed by the server. The connection itself IS the heartbeat -
// the server holds a Set of connected identities and broadcasts on every
// connect/disconnect (deduped by userId for signed-in users), plus a
// 5-second heartbeat to refresh the battles count.
//
// Reconnects with backoff if the socket drops. Hidden until the first
// message arrives so we never flash a misleading "0".

import { useEffect, useRef, useState } from 'react';
import { deviceId } from '~/lib/device-id';
import { env } from '~/lib/env';

type PresenceMsg = {
  type: 'presence';
  live: number; // distinct producers connected right now
  battles: number; // matches currently in flight
};

export function LiveActivityPill() {
  const [stats, setStats] = useState<PresenceMsg | null>(null);
  const [, setTick] = useState(0); // drives EQ randomness
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    const connect = async () => {
      if (cancelledRef.current) return;
      // Compute the device id once and pass it as ?fp= so the server can
      // dedup guests across tabs. Signed-in users get deduped by userId on
      // the server side regardless; this only affects guest connections.
      const fp = await deviceId();
      const url = `${env.VITE_WS_URL.replace(/\/$/, '')}/ws/presence${fp ? `?fp=${encodeURIComponent(fp)}` : ''}`;
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data) as PresenceMsg;
            if (msg && msg.type === 'presence') {
              setStats(msg);
              retryRef.current = 0;
            }
          } catch {
            // Ignore malformed messages; the next one will overwrite.
          }
        };
        ws.onclose = () => {
          if (cancelledRef.current) return;
          // Exponential backoff capped at 30s. The pill missing a few
          // seconds of updates is fine.
          const wait = Math.min(30_000, 500 * 2 ** retryRef.current++);
          window.setTimeout(() => void connect(), wait);
        };
        ws.onerror = () => {
          // onerror fires before onclose; let onclose drive the retry
          // so we don't double-schedule.
        };
      } catch {
        const wait = Math.min(30_000, 500 * 2 ** retryRef.current++);
        window.setTimeout(() => void connect(), wait);
      }
    };
    void connect();

    return () => {
      cancelledRef.current = true;
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, []);

  // Drive the EQ bars so they look like live audio rather than a synced
  // loading indicator.
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 160);
    return () => window.clearInterval(id);
  }, []);

  if (!stats) return null;

  const live = stats.live;
  const battles = stats.battles;
  const lit = live > 0;

  return (
    <div className="hidden items-center gap-2 rounded-full border border-border/70 bg-muted/30 px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider text-muted-foreground sm:inline-flex">
      <span className="relative flex size-1.5 items-center justify-center" aria-hidden>
        <span
          className={`absolute inline-block size-1.5 rounded-full ${lit ? 'animate-ping bg-emerald-400/60' : 'bg-muted-foreground/40'}`}
        />
        <span
          className={`relative inline-block size-1 rounded-full ${lit ? 'bg-emerald-400' : 'bg-muted-foreground/60'}`}
        />
      </span>
      <EqBars active={lit} />
      <span className="text-foreground">
        {live}
        <span className="text-muted-foreground"> live</span>
      </span>
      {battles > 0 && (
        <span className="text-muted-foreground/70">
          · {battles} {battles === 1 ? 'battle' : 'battles'}
        </span>
      )}
    </div>
  );
}

// 5 vertical bars whose heights jiggle on a CSS keyframe with staggered
// delays. Each bar uses its own animation so they don't move in lockstep -
// if they did it'd read as a loading skeleton instead of live activity.
function EqBars({ active }: { active: boolean }) {
  return (
    <span className="flex h-3 w-7 items-end gap-[1.5px]" aria-hidden>
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className="w-[2.5px] rounded-[1px] bg-primary"
          style={
            active
              ? {
                  animation: 'eq-bounce 0.9s ease-in-out infinite',
                  animationDelay: `${i * 0.11}s`,
                  height: '40%',
                }
              : { height: '15%', opacity: 0.5 }
          }
        />
      ))}
    </span>
  );
}
